/**
 * Pure H OS · Pre-Production Gap Closure · Blind Sample Scan Portal.
 * Panelists open /pureh/blind/scan/:code to see the blinded sample
 * metadata and record a quick score for the six standard dimensions.
 * Everything is authenticated — this is not a public route, but it's
 * mobile-friendly and QR-code addressable.
 */
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, SectionHeader, Button } from "../components/lib";
import { BLIND_PANELS } from "../mocks/rndData";
import { api } from "../services/api";

const DIMS = ["longevity", "sillage", "projection", "wearability", "uniqueness", "memorability"] as const;

export default function BlindScanPortal() {
  const { code } = useParams<{ code: string }>();
  const sample = useMemo(() => BLIND_PANELS.find((p) => p.id === code), [code]);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [note, setNote] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);

  if (!sample) {
    return (
      <div className="max-w-md mx-auto py-16 px-6" data-testid="blind-scan-not-found">
        <Card>
          <SectionHeader eyebrow="Blind Scan" title="Sample not found" />
          <div className="text-sm mt-2" style={{ color: "var(--ph-muted)" }}>
            The code <code>{code}</code> does not match any active blind sample.
            Ask the perfumer to reissue the QR.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8 px-4 space-y-5" data-testid="blind-scan-portal">
      <Card testId="scan-header">
        <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ph-muted)" }}>Blind Panel {sample.panel}</div>
        <div className="mt-1 text-lg font-semibold" style={{ color: "var(--ph-text)" }}>Sample <code>{sample.id}</code></div>
        <div className="text-xs mt-1" style={{ color: "var(--ph-muted)" }}>
          Panel size {sample.panelSize} · responses in {sample.responsesIn}
        </div>
      </Card>

      {submitted ? (
        <Card testId="scan-thanks">
          <div className="text-sm" style={{ color: "var(--ph-text)" }}>
            Thanks — your score is recorded. Continue to the next vial.
          </div>
          <div className="mt-3 text-xs" style={{ color: "var(--ph-muted)" }}>
            Avg: {(Object.values(scores).reduce((a, b) => a + b, 0) / Math.max(Object.values(scores).length, 1)).toFixed(1)} / 10
          </div>
        </Card>
      ) : (
        <>
          <Card testId="scan-scores">
            <div className="text-xs mb-3" style={{ color: "var(--ph-muted)" }}>Score each dimension 1–10</div>
            <div className="space-y-3">
              {DIMS.map((d) => (
                <div key={d}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>{d}</div>
                    <div className="text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{scores[d] ?? "—"}</div>
                  </div>
                  <input
                    type="range" min={1} max={10} step={1}
                    value={scores[d] ?? 5}
                    onChange={(e) => setScores({ ...scores, [d]: Number(e.target.value) })}
                    className="w-full"
                    data-testid={`scan-slider-${d}`}
                  />
                </div>
              ))}
            </div>
          </Card>

          <Card testId="scan-note">
            <div className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: "var(--ph-muted)" }}>Free note (optional)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full text-sm border rounded p-2 outline-none"
              style={{ borderColor: "var(--ph-border-subtle)", background: "var(--ph-surface)", color: "var(--ph-text)" }}
              placeholder="What did you smell?"
              data-testid="scan-note-input"
            />
          </Card>

          <Button
            variant="primary"
            onClick={async () => {
              // Persist the anonymous evaluation by patching the blind panel record.
              // We never expose the panelist; only aggregate counters + a note trail advance.
              try {
                const avg = Object.values(scores).reduce((a, b) => a + b, 0) / Math.max(1, Object.values(scores).length);
                await api.patch("blind_panels", sample.id, {
                  responsesIn: (sample.responsesIn ?? 0) + 1,
                  // Rolling average on `overall` — server-side would recompute; UI stores hint.
                  overall: Number(((sample.overall * sample.responsesIn + avg) / Math.max(1, sample.responsesIn + 1)).toFixed(2)),
                }, note ? `Blind scan note: ${note.slice(0, 240)}` : undefined);
              } catch {
                // Portal is designed to be resilient; ignore transient network issues.
              }
              setSubmitted(true);
            }}
            disabled={Object.keys(scores).length < DIMS.length}
            testId="scan-submit"
          >
            {Object.keys(scores).length < DIMS.length ? `Score ${DIMS.length - Object.keys(scores).length} more` : "Submit score"}
          </Button>
        </>
      )}
    </div>
  );
}
